import { isNaN } from 'lodash';
/**
 * @param {Object} ticket Object representing ticket
 * @returns {Array} bets array with selections mapped on to every bet
 */
const prepareBetSelections = (ticket) => {
  const { selections, bets } = ticket;

  bets.forEach((bet) => {
    const betSelections = bet.selectionRefs.map(({ selectionIndex }) => {
      const selection = selections[selectionIndex] || {};
      return selection;
    });
    // eslint-disable-next-line no-param-reassign
    bet.selections = betSelections;
  });
  return bets;
};

const mapStatus = (status, translations) => {
  const statusName = val => translations[`ticket_status_${val}`] || val;

  switch (status) {
    case 'ACCEPTED':
    case 'OPEN':
      return { id: '0', value: 'OPEN', name: statusName('open') };
    case 'WON':
      return { id: '2', value: 'WON', name: statusName('won') };
    case 'LOST':
      return { id: '4', value: 'LOST', name: statusName('lost') };
    case 'PAIDOUT':
      return { id: '5', value: 'PAYEDOUT', name: statusName('paidout') };
    case 'IN_PLAY':
      return { id: '8', value: 'IN_PLAY', name: statusName('in_play') };
    case 'EXPIRED':
      return { id: '13', value: 'EXPIRED', name: statusName('expired') };
    default:
      return { id: '-1', value: status, name: status ? status.toLowerCase() : status };
  }
};

// TODO: Colors for NEXT_COLOR_BET (marketId === 9) should be loaded dynamically from API
const colorMap = {
  1: 'blue',
  2: 'green',
  3: 'purple',
  4: 'red',
  5: 'orange',
  6: 'black',
};

/**
 * @param {Array} tickets Array of tickets
 * @returns {Array} Array of tickets with top level ticket data with bets prop not mapped
 */
const mapTicketData = (tickets, translations) => tickets.map((ticket) => {
  const {
    codes, totals, status, createdAt, productName, bets, requestId, currency,
  } = ticket;
  const {
    taxes,
    payment,
    payout,
    stake,
    possiblePayout,
    maximumPossiblePayout,
  } = totals;
  const id = codes?.find(code => code.type === 'barcode')?.id;
  const payoutValue = status === 'WON' ? possiblePayout.real.value : payout.real.value;
  const payinTaxes = taxes?.filter(tax => tax.type === 'PAYIN');
  const payinTax = payinTaxes.reduce((sum, tax) => sum + tax.value, 0);
  const payoutTaxes = taxes?.filter(tax => tax.type === 'PAYOUT');
  const payoutTax = payoutTaxes.reduce((sum, tax) => sum + tax.value, 0);
  const winnings = bets.reduce(
    (totalWinnings, bet) => totalWinnings + (bet.winnings?.total.value || 0),
    0,
  );
  const minPossiblePayout = totals.minimumPossiblePayout?.real?.value || null;
  const minPossiblePayoutTax = totals.minimumPossiblePayout?.taxes?.reduce((acc, tax) => (tax.type === 'PAYOUT' ? acc + tax.value : acc), 0);
  const maxPossiblePayout = maximumPossiblePayout?.real.value || null;
  const maxPossiblePayoutTax = maximumPossiblePayout?.taxes?.reduce((acc, tax) => (tax.type === 'PAYOUT' ? acc + tax.value : acc), 0);
  const mappedStatus = mapStatus(ticket.status, translations);
  const maxPossibleWin = totals.winnings.maxPossible || null;

  const mappedTicket = {
    // USED PROPS THAT ARE MAPPED
    id,
    bets: prepareBetSelections(ticket),
    payout: payoutValue,
    payin: payment.real.value,
    payinTax,
    payoutTax,
    createdAt,
    status: mappedStatus,
    product: productName,
    maxPossibleWin,
    winnings,
    requestUuid: requestId,
    possiblePayout: minPossiblePayout,
    possiblePayoutTax: minPossiblePayoutTax,
    maxPossiblePayoutTax,
    maxPossiblePayout,
    bet: stake.real.value,
    // USED PROPS THAT ARENT MAPPED
    isFreeBet: null,
    taxData: null,
    // POTENTIALLY USED PROPS
    // PROPS THAT CAN BE MAPPED
    ticketDateTimeUTC: createdAt,
    ticketDateTime: createdAt,
    superBonus: {
      name: null,
      amount: null,
    },
    taxAuthorityIn: null,
    taxAuthorityOut: null,
    responseMessage: null,
    productVersion: null,
    playerUserName: null,
    playerLastName: null,
    playerId: null,
    playerFirstName: null,
    paymentId: null,
    payedOutAt: null,
    payedOutAtUTC: null,
    operatorUserName: null,
    operatorLastName: null,
    operatorId: null,
    operatorFirstName: null,
    deviceName: null,
    deviceId: null,
    deliveryPlatform: null,
    currency,
    cpvUuid: null,
    cashRegisterName: null,
    cashRegisterUuid: null,
    cancelledPayinTax: null,
    cancelledAmount: null, // can be removed
    bonuses: null,
    bonusType: null,
    betshopName: null,
    betshopId: null,
    betshopGroupName: null,
    betshopGroupId: null,
    action: null,
    deviceNames: { // found in keno shop payin template header
      value: null,
    },
  };
  return mappedTicket;
});

const mapNewToNgsTicketLuckySix = (tickets, translations = {}) => {
  // eslint-disable-next-line
  console.log('Map new to ngs ticket LuckySix', tickets);
  const ticketData = mapTicketData(tickets, translations);

  ticketData.forEach((ticket) => {
    // eslint-disable-next-line no-param-reassign
    ticket.bets = ticket.bets.map((bet) => {
      // outcomeName unused
      const {
        marketId, outcomeId, eventDisplayId, odds, marketName, eventStartTime, outcomeName,
      } = bet.selections[0];

      const { status } = bet;
      const betTax = bet.taxes?.filter(tax => tax.type === 'PAYIN').reduce((total, tax) => total + tax.value, 0);
      const eventValue = bet.selections[0].result ?? '';

      return {
        id: bet.id,
        status: mapStatus(status, translations),
        type: marketId,
        typeValue: translations[marketName] || marketName,
        // category
        value: outcomeId,
        amount: bet.payment.real.value,
        winnings: bet.winnings.real.value,
        // cancelledAmount
        eventId: eventDisplayId,
        // realEventId
        // eventIds
        // productVersion
        // bonus
        odd: odds,
        eventValue,
        possibleWinnings: bet.winnings.maxPossible,
        eventDateTime: new Date(eventStartTime).getTime(),
        eventDateTimeUTC: new Date(eventStartTime).getTime(),
        tax: betTax,
        guid: null,
        possibleTax: bet.payout.payoutTax,
        possiblePayout: bet.minimumPossiblePayout.real.value,
        payin: bet.payment.real.value,
        bet: {
          id: marketId,
          title: translations[marketName] || marketName,
        },
        outcome: {
          value: outcomeId,
          title: translations[outcomeName] || outcomeName,
        },
      };
    });
  });

  return ticketData;
};
const mapNewToNgsTicketRacers = (tickets, translations = {}) => {
  // eslint-disable-next-line
  console.log('Map new to ngs ticket Racers', tickets);
  const ticketsData = mapTicketData(tickets, translations);

  // TODO MAP BETS DIFFERENTLY FOR EACH GAME
  ticketsData.forEach((ticket) => {
    const mappedBets = [];
    ticket.bets.forEach((bet) => {
      const mappedBet = {};
      // eslint-disable-next-line
      const betSelections = bet.selections;
      const status = mapStatus(bet.status, translations);
      const betName = {
        title: translations[betSelections[0].marketName] || betSelections[0].marketName,
        id: betSelections[0].marketId,
      };
      const outcome = {
        title: translations[betSelections[0].outcomeName] || betSelections[0].outcomeName,
        value: betSelections[0].outcomeId,
      };
      // bet mapping
      mappedBet.bet = betName;
      mappedBet.amount = bet.stake.real.value;
      mappedBet.eventId = betSelections[0].eventDisplayId;
      mappedBet.id = betSelections[0].id;
      mappedBet.odd = betSelections[0].odds;
      mappedBet.outcome = outcome;
      mappedBet.payin = bet.payment.real.value;
      mappedBet.possiblePayout = bet.minimumPossiblePayout.real.value;
      mappedBet.possibleTax = null; // TODO
      mappedBet.possibleWin = bet.winnings.total.value;
      mappedBet.realEventId = betSelections[0].eventId;
      mappedBet.status = status;
      mappedBet.tax = null; // TODO
      mappedBet.type = betSelections[0].marketId;
      mappedBet.value = betSelections[0].outcomeId.split('-');
      mappedBet.winnings = bet.winnings.total.value;
      // Might be changed later
      mappedBet.combinations = [{
        odd: betSelections[0].odds,
        value: betSelections[0].outcomeId,
      }];
      // mappedBet.bonus = null;
      // mappedBet.cancelledAmount = null;
      // mappedBet.category = null;
      // mappedBet.eventDateTime = null;
      // mappedBet.eventDateTimeUTC = null;
      // mappedBet.eventIds = null;
      // mappedBet.eventValue = null;
      // mappedBet.guid = null;
      // mappedBet.limit = null;
      // mappedBet.productVersion = null;
      // mappedBet.typeValue = null;
      mappedBets.push(mappedBet);
    });
    // eslint-disable-next-line
    ticket.bets = mappedBets;
  });
  return ticketsData;
};

const mapNewToNgsTicketKeno = (tickets, translations = {}) => {
  const ticketData = mapTicketData(tickets, translations);

  ticketData.forEach((ticket) => {
    // eslint-disable-next-line no-param-reassign
    ticket.bets = ticket.bets.map((bet) => {
      const {
        marketId, outcomeId, eventDisplayId, marketName, outcomeName, eventStartTime,
      } = bet.selections[0];
      const { status } = bet;
      let value;
      let system;
      // Check markets which include an array
      if ([...Array(16).keys(), 40, 41].includes(marketId)) {
        if (marketId < 16) {
          // Split by ':' to separate system if exists
          const [numbersPart, systemPart] = outcomeId.split(':');
          value = numbersPart.split(',').map(Number);
          if (systemPart) system = Number(systemPart);
        } else {
          // For no draw (40) and all in (41)
          value = outcomeId.split(',').map(Number);
        }
      } else {
        value = Number(outcomeId);
      }
      // Added because the new implementation offers odds for spot marktets
      // which is not technically correct and its better to hide them on the UI.
      // eslint-disable-next-line max-len
      const mapOdds = selection => (selection.marketId >= 30 ? selection.odds || selection.minimumOdds : null);
      const betTax = bet.taxes?.filter(tax => tax.type === 'PAYIN').reduce((total, tax) => total + tax.value, 0);
      const eventValue = bet.selections[0].result ?? '';
      const mappedOdds = mapOdds(bet.selections[0]);
      return {
        id: bet.id,
        amount: bet.payment.real.value,
        winnings: bet.winnings.real.value,
        status: mapStatus(status, translations),
        type: marketId,
        possibleWinnings: bet.winnings.maxPossible,
        value,
        system,
        eventId: eventDisplayId,
        // realEventId
        tax: betTax,
        odds: mappedOdds,
        bet: {
          id: marketId,
          title: translations[marketName] || marketName,
        },
        outcome: {
          value,
          title: translations[outcomeName] || outcomeName,
          drawn: eventValue.split(',').map(Number),
        },
        eventDateTime: new Date(eventStartTime).getTime(),
        eventDateTimeUTC: new Date(eventStartTime).getTime(),
        possiblePayout: bet.minimumPossiblePayout.real.value,
        payin: bet.payment.real.value,
        guid: null,
        possibleTax: bet.payout.payoutTax,
      };
    });
  });

  return ticketData;
};

const mapNewToNgsTicketVps = (tickets, translations) => {
  // eslint-disable-next-line
  console.log('Map new to ngs ticket VPS', tickets);
  const ticketsData = mapTicketData(tickets, translations);
  // TODO MAP BETS DIFFERENTLY FOR EACH GAME
  const bets = [];
  ticketsData.bets = bets;
  return ticketsData;
};
const mapNewToNgsTicketNextSix = (tickets, translations = {}) => {
  // eslint-disable-next-line
  console.log('Map new to ngs ticket Next Six', tickets);
  const ticketsData = mapTicketData(tickets, translations);

  ticketsData.forEach((ticket) => {
    // eslint-disable-next-line no-param-reassign
    ticket.bets = ticket.bets.map((bet) => {
      const selection = bet.selections[0];

      const {
        marketId,
        marketName,
        outcomeId,
        outcomeName,
        eventDisplayId,
        odds,
        result,
      } = selection;

      let outcomeValue;
      if (typeof outcomeId === 'string' && outcomeId.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(outcomeId);
          outcomeValue = Array.isArray(parsed) && parsed.length === 1 ? parsed[0] : parsed;
        } catch {
          outcomeValue = outcomeId;
        }
      } else {
        outcomeValue = outcomeId;
      }

      if (marketId === 9 && !isNaN(outcomeValue)) {
        outcomeValue = translations[colorMap[outcomeValue]] || outcomeName;
      }

      let drawn = [];
      try {
        drawn = typeof result === 'string' ? JSON.parse(result) : [];
      } catch {
        drawn = [];
      }

      const betTax = bet.taxes
        ?.filter(tax => tax.type === 'PAYIN')
        .reduce((sum, tax) => sum + tax.value, 0) || 0;


      return {
        id: bet.id,
        status: mapStatus(bet.status, translations),
        amount: bet.payment.real.value,
        payin: bet.payment.real.value,
        winnings: bet.winnings.real.value,
        type: marketId,
        typeValue: marketName,
        value: outcomeId,
        eventId: eventDisplayId,
        odd: odds,
        bet: {
          id: marketId,
          title: translations[marketName] || marketName,
        },
        outcome: {
          title: translations[outcomeValue] || outcomeValue,
          value: outcomeValue,
          drawn,
        },
        eventDateTime: new Date(ticket.createdAt).getTime(),
        eventDateTimeUTC: new Date(ticket.createdAt).getTime(),
        tax: betTax,
        possiblePayout: bet.minimumPossiblePayout?.real?.value || 0,
        possibleTax: bet.payout?.payoutTax || 0,
        possibleWinnings: null,
        guid: null,
      };
    });
  });
  return ticketsData;
};
const mapNewToNgsTicketLuckyX = (tickets, translations = {}) => {
  // eslint-disable-next-line
  console.log('Map new to ngs ticket Lucky X', tickets);
  const ticketsData = mapTicketData(tickets, translations);

  ticketsData.forEach((ticket) => {
    // eslint-disable-next-line no-param-reassign
    ticket.bets = ticket.bets.map((bet) => {
      const {
        marketId, outcomeId, eventDisplayId, odds, marketName, eventStartTime,
      } = bet.selections[0];

      const { status } = bet;
      const betTax = bet.taxes?.filter(tax => tax.type === 'PAYIN').reduce((total, tax) => total + tax.value, 0);
      const eventValue = bet.selections[0].result ?? '';

      return {
        id: bet.id,
        status: mapStatus(status, translations),
        type: marketId,
        typeValue: translations[marketName] || marketName,
        value: outcomeId,
        amount: bet.payment.real.value,
        winnings: bet.winnings.real.value,
        eventId: eventDisplayId,
        odd: odds,
        eventValue,
        possibleWinnings: bet.winnings.maxPossible,
        eventDateTime: new Date(eventStartTime).getTime(),
        eventDateTimeUTC: new Date(eventStartTime).getTime(),
        tax: betTax,
        guid: null,
        possibleTax: bet.payout.payoutTax,
        possiblePayout: bet.minimumPossiblePayout.real.value,
        payin: bet.payment.real.value,
      };
    });
  });
  return ticketsData;
};
/**
 * @param {Array} tickets Array of ticket objects
 * @param {string} gameName String representing game name all lowercase and without spaces
 * @param {Object} translations Optional translations object for outcome names
 * @returns {Array} Array of mapped ticket objects
 */
const mapNewToNgsTicket = (tickets, gameName, translations = {}) => {
  let mappedTickets = {};
  switch (gameName) {
    case 'luckysix':
    case 'LuckySixV2':
      mappedTickets = mapNewToNgsTicketLuckySix(tickets, translations);
      break;
    case 'GreyhoundRaces':
    case 'GreyhoundRacesV2':
    case 'virtualgreyhoundraces':
    case 'VirtualGreyhoundRacesV2':
    case 'virtualhorseraces':
    case 'VirtualHorseRacesV2':
    case 'virtualmotorcyclespeedway':
    case 'VirtualMotorcycleSpeedwayV2':
      mappedTickets = mapNewToNgsTicketRacers(tickets, translations);
      break;
    case 'keno':
    case 'KenoV2':
      mappedTickets = mapNewToNgsTicketKeno(tickets, translations);
      break;
    case 'virtualpenaltyshootout':
      mappedTickets = mapNewToNgsTicketVps(tickets, translations);
      break;
    case 'nextsix':
    case 'NextSixV2':
      mappedTickets = mapNewToNgsTicketNextSix(tickets, translations);
      break;
    case 'luckyx':
    case 'LuckyXV2':
      mappedTickets = mapNewToNgsTicketLuckyX(tickets, translations);
      break;
    default:
      break;
  }
  return mappedTickets;
};

const mapAcceptedIntoInPlayTickets = (tickets, roundId, translations = {}) => (
  tickets.map((ticket) => {
    // Used on the start of the round to map OPEN tickets with OPEN bets to IN_PLAY.
    // This is needed because the Read API is missing the IN_PLAY status and
    // client applications rely on the status to handle some scenarios.
    const hasInPlayBet = ticket.bets.some(
      bet => bet.status.value === 'OPEN' && Number(bet.eventId) === Number(roundId),
    );
    if (ticket.status.value !== 'OPEN' || !hasInPlayBet) return ticket;
    return {
      ...ticket,
      status: mapStatus('IN_PLAY', translations),
    };
  })
);
export { mapNewToNgsTicket, mapAcceptedIntoInPlayTickets };
